import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { DatovkaApiType, OsobyKomunikacniUdajeDto } from '@gov-nx/api';
import { useAuthorizationConsentYupSchema } from '@gov-nx/core/hooks';
import { useLocale, useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	cleanPhoneNumber,
	useAutocompleteDefinition,
	useDataBoxShape,
	useEmailShape,
	usePhoneShape,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import type { DataRequest, FormData } from './service.types';

export interface FormInstanceProps {
	code: ServiceCode;
	dataBoxTypes: DatovkaApiType['typSchranky'][];
	person?: OsobyKomunikacniUdajeDto;
	isDisabled: boolean;
	isLowAuthorization: boolean;
}

export function FormInstance({
	code,
	person,
	dataBoxTypes,
	isDisabled,
	isLowAuthorization,
}: FormInstanceProps): FormDefinition<FormData> {
	const { getLocalize } = useTranslationWithNamespace();
	const { getDataBoxShape, getDataBoxDefaultValues } = useDataBoxShape({
		types: dataBoxTypes,
	});
	const { datovaSchrankaId, ...dataBoxAdditional } = getDataBoxShape({
		isRequired: false,
	});
	const { t } = useLocale(code);
	const { getPhoneShape } = usePhoneShape();
	const { getEmailShape } = useEmailShape();
	const adresatDSId = useAutocompleteDefinition({
		name: 'adresatDSId',
		isDisabled: isDisabled,
		placeholder: t('formular.placeholders.autocomplete'),
		label: t('formular.pole.adresat'),
		requiredMessage: t('formular.validace.adresat.vyberte'),
	});

	const consentSchema = useAuthorizationConsentYupSchema({
		isConsentRequired: isLowAuthorization,
	});

	const formSchema = yup
		.object({
			adresatDSId: adresatDSId.schema,
			odesilatelDSId: datovaSchrankaId,
			...dataBoxAdditional,
			telefon: getPhoneShape(),
			email: getEmailShape(),
			predmet: yup
				.string()
				.required(getLocalize(code, 'formular.validace.predmet.povinny')),
			text: yup
				.string()
				.required(getLocalize(code, 'formular.validace.zprava.povinny')),
		})
		.shape({ __souhlas: consentSchema })
		.required();

	const dataBoxDefaultValues = getDataBoxDefaultValues();

	const formMethods = useGovForm<FormData>({
		formSchema,
		defaultValues: {
			odesilatelDSId: dataBoxDefaultValues.datovaSchrankaId,
			naseCisloJednaci: dataBoxDefaultValues.naseCisloJednaci,
			naseSpisovaZnacka: dataBoxDefaultValues.naseSpisovaZnacka,
			adresatDSId: adresatDSId.defaultValue,

			telefon: person?.telefonniCislo,
			email: person?.email,
			predmet: undefined,
			text: undefined,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export const prepareSubmitData = (data: FormData): DataRequest => ({
	params: {
		adresatDSId: data.adresatDSId?.selected?.datovaSchrankaId,
		naseCisloJednaci: data.naseCisloJednaci,
		naseSpisovaZnacka: data.naseSpisovaZnacka,
		odesilatelDSId: data.odesilatelDSId,
	},
	body: {
		kodPodani: 'ZADOST_O_INFORMACE',
		email: data.email,
		telefon: data.telefon ? cleanPhoneNumber(data.telefon) : undefined,
		predmet: data.predmet,
		text: data.text,
	},
});
